<script lang="ts" setup>
  import { useVoucherStore } from '@/components/fsf/voucher/store'
  import { storeToRefs } from 'pinia'

  const { t } = useI18n()
  const { isDesktop } = useBTBreakpoints()

  const voucherStore = useVoucherStore()
  const { vouchers, forceOpen, canAddVoucher, vouchersCount, hasVoucherErrors } = storeToRefs(voucherStore)

  const isOpen = ref(forceOpen.value)

  withDefaults(
    defineProps<{
      mobile?: boolean
    }>(),
    {
      mobile: false
    }
  )

  voucherStore.checkAllVouchersForDuplicates(vouchers.value, true)

  onMounted(() => {
    if (hasVoucherErrors.value || forceOpen.value) {
      isOpen.value = true
      nextTick(() => {
        const el = document.querySelector('[data-voucher]') as HTMLElement | null
        el?.scrollIntoView({ block: 'center', behavior: 'instant', inline: 'center' })
      })
    }
  })

  const closeInputModal = () => {
    if (hasVoucherErrors.value) {
      isOpen.value = true

      return
    }

    isOpen.value = false
    voucherStore.removeEmptyVouchers()
  }

  const canRemoveVoucher = computed(() => vouchers.value.length > 1 || vouchers.value[0].inputValue !== '')

  const voucherText = computed(() =>
    vouchersCount.value >= 1 ? t('vouchers.addedVouchersCount', vouchersCount.value) : t('vouchers.title')
  )

  const onRemoveVoucher = (index: number) => {
    voucherStore.removeVoucherField(index)

    nextTick(() => {
      focusNextVoucher(index)
    })
  }

  const focusNextVoucher = (index: number): void => {
    for (let i = index; i >= 0; i--) {
      const nextVoucher = document.querySelector<HTMLInputElement>(`[name="voucher-${i}"]`)
      if (nextVoucher) {
        nextVoucher.focus()
        break
      }
    }
  }

  const onAddVoucher = () => {
    voucherStore.addVoucherField()

    nextTick(() => {
      const lastVoucher = document.querySelector<HTMLInputElement>(`[name="voucher-${vouchers.value.length - 1}"]`)
      lastVoucher?.focus()
    })
  }
</script>
<template>
  <div
    data-voucher
    class="relative mt-2 md:mt-0"
  >
    <UiButton
      icon="common/gift"
      variant="border"
      size="lg"
      class="w-full text-base font-normal leading-6 text-white md:w-fit md:rounded-xs md:text-bt-blue-ui"
      :class="{ 'justify-center': mobile }"
      :title="voucherText"
      :label="$t('vouchers.title')"
      @click="isOpen = true"
      @keydown.prevent.enter="isOpen = true"
    />

    <UiModalInput
      v-model="isOpen"
      :title="$t('vouchers.title')"
      :show-close="!isDesktop"
      :has-offset="isDesktop"
      side-y="top"
      :dialog-content-class="'md:w-90'"
      @update:model-value="closeInputModal"
    >
      <template #content>
        <div class="overflow-auto px-6 pb-6 pt-4">
          <div class="flex w-full flex-col gap-2">
            <div
              v-for="(input, index) in vouchers"
              :key="index"
              class="relative flex w-full cursor-pointer items-start gap-2"
            >
              <UiInput
                v-model.trimallspaces="input.inputValue"
                class="w-full"
                path="voucherCode"
                input-key="voucherCode"
                can-click-icon
                :name="`voucher-${index}`"
                :error="input.error"
                :icon="canRemoveVoucher ? 'common/close' : undefined"
                :max-length="20"
                @icon-click="onRemoveVoucher(index)"
                @update:model-value="voucherStore.validateVoucher(input)"
              />
            </div>
            <div
              class="flex flex-col items-end gap-2 md:flex-row md:justify-end"
              :class="{ 'md:justify-between': canAddVoucher }"
            >
              <UiButton
                v-if="canAddVoucher"
                class="w-fit !px-0"
                variant="link"
                size="lg"
                :title="$t('vouchers.addVoucher')"
                @click="onAddVoucher"
              />

              <UiButton
                class="w-fit"
                variant="secondary"
                size="lg"
                :title="$t('save')"
                @click="closeInputModal"
              />
            </div>
          </div>
        </div>
      </template>
    </UiModalInput>
  </div>
</template>
